import './UnitTile.scss';
import React from 'react';

import { Badge } from 'src/component/ds/ui/badge';
import { SEVERITY_ICON, SEVERITY_STYLE } from 'src/screen/v2/Protected/DashboardScreen/AlarmsToTriage/severity';
import { cn } from 'src/component/ds/utils';
import { useTranslation } from 'src/i18n';
import type { UnitAlarmCount } from './alarmVolume';

export interface UnitTileProps {
  unit: string;
  counts: UnitAlarmCount[];
  selected?: boolean;
  onSelect?: (unit: string) => void;
}

/**
 * One area in the Alarm Volume grid: its name, and a badge per severity that
 * currently has alarms. Selecting it filters the dashboard to that area.
 *
 * @param {UnitTileProps} props see UnitTileProps
 * @returns {JSX.Element} the tile button
 */
export default function UnitTile ({ unit, counts, selected, onSelect }: UnitTileProps) {
  const { t } = useTranslation();
  const severitiesWithAlarms = counts.filter(({ count }) => count > 0);

  return (
    <button
      type='button'
      onClick={() => onSelect?.(unit)}
      aria-pressed={Boolean(selected)}
      className={cn(
        'alarm-volume__tile',
        'ds-flex ds-min-h-[89px] ds-min-w-0 ds-flex-col ds-justify-center ds-gap-1 ds-rounded ds-p-4 ds-text-left',
        'ds-cursor-pointer ds-border ds-bg-muted ds-shadow-sm',
        selected ? 'ds-border-primary' : 'ds-border-border',
        'focus-visible:ds-outline-none focus-visible:ds-shadow-chemelex-focus'
      )}
      data-testid={`alarm-volume-tile-${unit}`}
    >
      <span className='ds-truncate ds-font-heading ds-text-chemelex-h3 ds-font-medium ds-text-foreground' title={unit}>
        {unit}
      </span>

      <span className='alarm-volume__badges ds-flex ds-flex-nowrap ds-items-center ds-gap-1'>
        {severitiesWithAlarms.length
          ? severitiesWithAlarms.map(({ severity, count, label }) => {
            const Icon = SEVERITY_ICON[severity];

            return (
              <Badge
                key={severity}
                variant={null}
                className={cn('alarm-volume__badge', SEVERITY_STYLE[severity].bar, 'ds-text-primary-foreground')}
                aria-label={t('dashboard.alarmVolumeBadgeLabel', { count, label })}
                data-testid={`alarm-volume-badge-${severity}`}
              >
                <Icon aria-hidden='true' fontSize='inherit' />
                {count}
              </Badge>
            );
          })
          : (
            <span className='ds-text-xs ds-text-muted-foreground'>
              {t('dashboard.alarmVolumeNoAlarms')}
            </span>
          )}
      </span>
    </button>
  );
}
